import React from 'react';
import { FormControl, Select, MenuItem, InputLabel } from '@material-ui/core';
import '../../styles/MonthYearPicker.css';
import { monthNames } from '../../utils/utils';

interface MonthYearPickerProps {
  value: string; // Format: "YYYY-MM"
  onChange: (value: string) => void;
  minDate?: string; // Format: "YYYY-MM"
  maxDate?: string; // Format: "YYYY-MM"
}

export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value,
  onChange,
  minDate = '2026-06',
  maxDate,
}) => {
  // Parse current value
  const [year, month] = value.split('-').map(Number);

  // Get current date for max constraint
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [minYear, minMonth] = minDate.split('-').map(Number);
  const [maxYear, maxMonth] = maxDate
    ? maxDate.split('-').map(Number)
    : [currentYear, currentMonth];

  // Generate year options (from minYear to maxYear)
  const yearOptions = [];
  for (let y = minYear; y <= maxYear; y++) {
    yearOptions.push(y);
  }

  // Generate month options based on selected year
  const getAvailableMonths = (selectedYear: number) => {
    const months = [];
    for (let m = 1; m <= 12; m++) {
      // Check if month is within min/max range
      const isAfterMin =
        selectedYear > minYear || (selectedYear === minYear && m >= minMonth);
      const isBeforeMax =
        selectedYear < maxYear || (selectedYear === maxYear && m <= maxMonth);

      if (isAfterMin && isBeforeMax) {
        months.push(m);
      }
    }
    return months;
  };

  const availableMonths = getAvailableMonths(year);

  const handleYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newYear = event.target.value as number;
    const availableMonthsForYear = getAvailableMonths(newYear);

    // If current month is not available for new year, select the first available month
    let newMonth = month;
    if (!availableMonthsForYear.includes(month)) {
      newMonth = availableMonthsForYear[0];
    }

    onChange(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newMonth = event.target.value as number;
    onChange(`${year}-${String(newMonth).padStart(2, '0')}`);
  };

  return (
    <div className="month-year-picker-container">
      <FormControl
        variant="outlined"
        className="month-year-picker-form-control month-control"
      >
        <InputLabel id="month-label">Month</InputLabel>
        <Select
          labelId="month-label"
          value={month}
          onChange={handleMonthChange}
          label="Month"
        >
          {availableMonths.map(m => (
            <MenuItem key={m} value={m}>
              {monthNames[m - 1]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        variant="outlined"
        className="month-year-picker-form-control year-control"
      >
        <InputLabel id="year-label">Year</InputLabel>
        <Select
          labelId="year-label"
          value={year}
          onChange={handleYearChange}
          label="Year"
        >
          {yearOptions.map(y => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
